import { cn } from "@win-reality/react-ui";
import { Loader2 } from "lucide-react";
import { ReactNode } from "react";

import { FeedPitch } from "../../utils/plateAppearanceFeed";
import { PitchStatus, pitchStatusLabel } from "../../utils/tagQueue";
import { getPitchOutcome, PitchSummary } from "./PitchSummary";

export const SELECTED_ARROW_CLIP =
  "polygon(0 0, calc(100% - 16px) 0, 100% 50%, calc(100% - 16px) 100%, 0 100%)";

type SelectableRowProps = {
  pitchNumber: number;
  isSelected?: boolean;
  /** Shows a spinner in place of the pitch number, e.g. while tags save */
  isPending?: boolean;
  onClick?: () => void;
  children: ReactNode;
  /** Right-aligned value, e.g. the count */
  trailing?: ReactNode;
  /** Test id and instance attributes for the row, from the call site */
  "data-testid"?: string;
  "data-event-uuid"?: string;
  "data-activity-id"?: string;
  "data-status"?: PitchStatus | "tagged" | "untagged";
  "data-value"?: number;
};

/** Pitch number badge + content, with the arrow highlight when selected. */
const SelectablePitchRow = ({
  pitchNumber,
  isSelected = false,
  isPending = false,
  onClick,
  children,
  trailing,
  ...attributes
}: SelectableRowProps) => (
  <div
    {...attributes}
    aria-current={isSelected ? "true" : undefined}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onClick={onClick}
    onKeyDown={(e) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick();
      }
    }}
    className={cn(
      // Layout
      "flex items-center gap-3",
      // Spacing
      "py-3 pl-3 pr-8",
      // Border
      "rounded-l-xl",
      // Background
      isSelected && "bg-tagging-card",
      // States
      onClick && "cursor-pointer",
    )}
    style={isSelected ? { clipPath: SELECTED_ARROW_CLIP } : undefined}
  >
    {/* Pitch number badge */}
    <div
      data-testid="plate-appearance-pitch-number-value"
      data-value={pitchNumber}
      aria-busy={isPending || undefined}
      className={cn(
        // Size
        "h-8 w-8 shrink-0",
        // Layout
        "flex items-center justify-center",
        // Border
        "rounded-full",
        // Background
        "bg-tagging-hotkey",
        // Text
        "text-sm font-bold text-gray-900 dark:text-white",
      )}
    >
      {isPending ? (
        <Loader2
          aria-label="Saving"
          className="h-4 w-4 animate-spin text-tagging-muted"
        />
      ) : (
        pitchNumber
      )}
    </div>

    <div className="min-w-0 flex-1 text-sm leading-snug">{children}</div>

    {trailing != null && (
      <div
        className={cn(
          "shrink-0 text-sm",
          isSelected ? "text-gray-900 dark:text-white" : "text-tagging-muted",
        )}
      >
        {trailing}
      </div>
    )}
  </div>
);

type PitchRowProps = {
  pitch: FeedPitch;
  isSelected?: boolean;
  /** Its tags are saving, or a queued tag is being sent for it */
  status?: PitchStatus;
  onClick?: () => void;
  onAddDetails?: () => void;
};

export const PitchRow = ({
  pitch,
  isSelected = false,
  status,
  onClick,
  onAddDetails,
}: PitchRowProps) => {
  const isPending = status != null;
  // Says why the row is locked while a queued tag updates its players
  const statusLabel = status ? pitchStatusLabel(status) : null;

  return (
    <SelectablePitchRow
      data-testid="plate-appearance-pitch-item"
      data-event-uuid={pitch.omnibusEventId}
      data-activity-id={pitch.activityId}
      data-status={status ?? (pitch.isTagged ? "tagged" : "untagged")}
      pitchNumber={pitch.pitchNumber}
      isSelected={isSelected}
      isPending={isPending}
      onClick={onClick}
      // The local count, runs and outs are a guess until the game service
      // sends the pitch back with its resulting state
      trailing={isPending ? undefined : getPitchOutcome(pitch.summary)}
    >
      <PitchSummary
        summary={pitch.summary}
        omnibusEventId={pitch.omnibusEventId}
        status={status}
        statusLabel={statusLabel}
        onAddDetails={onAddDetails}
      />
    </SelectablePitchRow>
  );
};

export type PitchListProps = {
  selectedActivityId?: string;
  /** Pitches whose tags are saving or queued */
  pitchStatuses?: ReadonlyMap<string, PitchStatus>;
  onSelectPitch?: (activityId: string) => void;
  onAddDetails?: (activityId: string) => void;
};

/** A plate appearance's pitches, newest first. */
export const PitchList = ({
  pitches,
  selectedActivityId,
  pitchStatuses,
  onSelectPitch,
  onAddDetails,
}: PitchListProps & { pitches: FeedPitch[] }) =>
  pitches.map((pitch) => (
    <PitchRow
      key={pitch.activityId}
      pitch={pitch}
      isSelected={pitch.activityId === selectedActivityId}
      status={pitchStatuses?.get(pitch.activityId)}
      onClick={
        onSelectPitch ? () => onSelectPitch(pitch.activityId) : undefined
      }
      onAddDetails={() => onAddDetails?.(pitch.activityId)}
    />
  ));

type WaitingPitchRowProps = {
  pitchNumber: number;
  isSelected?: boolean;
  onClick?: () => void;
};

/** Placeholder for the next pitch once the latest one has been tagged. */
export const WaitingPitchRow = ({
  pitchNumber,
  isSelected,
  onClick,
}: WaitingPitchRowProps) => (
  <SelectablePitchRow
    data-testid="plate-appearance-waiting-pitch-item"
    data-value={pitchNumber}
    pitchNumber={pitchNumber}
    isSelected={isSelected}
    onClick={onClick}
    trailing="--"
  >
    <div className="truncate text-gray-900 dark:text-white">
      Waiting on pitch...
    </div>
    <div className="text-tagging-muted">--</div>
  </SelectablePitchRow>
);
